import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Leaf, Loader2, CheckCircle2, Info, Search, ChevronDown, Lock } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { NumberInput } from '@/components/ui/number-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { NumberDisplay } from '@/components/ui/number-display';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useTranslation } from '@/hooks/useTranslation';
import {
  useCarbonTableColumns,
  useCarbonColumnValues,
  useFactorDimensionRows,
  useResolvedFactor,
  calculateCarbonEmission,
  useCompatibleMultiplierUnitsForDimensions,
} from '@/hooks/useCarbonConfig';
import { unitLabel, convertValue } from '@releve/shared/units';
import {
  computeDimensionCascade,
  DIMENSION_NONE_VALUE,
  resolveDimensionValueLabel,
  withNoneOption,
} from '@/lib/dimensionCascade';
import { useSaveEmissionFactorPick } from '@/hooks/useEmissionFactorPicks';
import { useEmissionFactorTableIndex } from '@/hooks/useEmissionFactorTableIndex';
import { invalidateEmissionFactorCaches } from '@/lib/emissionFactorCacheKeys';
import {
  EmissionFactorPickerDialog,
  type PickedEmissionFactor,
} from '@/components/emissions/EmissionFactorPickerDialog';
import { prettyLca, slugifyClimatiqDimension as slugify } from '@/lib/climatiqUnitTypes';
import { toast } from '@/hooks/useToast';
import { captureException } from '@/lib/sentry';

/** The four fields that define one carbon impact: a resolved factor + a quantity. */
export interface CarbonImpactValue {
  emissionTableId: string;
  dimensionValues: Record<string, string>;
  quantity: number;
  /** Canonical unit code (e.g. 'kg', 'km', 'passenger_mile') the user entered. */
  quantityUnit: string;
}

export const EMPTY_CARBON_IMPACT: CarbonImpactValue = {
  emissionTableId: '',
  dimensionValues: {},
  quantity: 0,
  quantityUnit: '',
};

/**
 * The picker's authoritative, self-consistent report of the resolved factor. It
 * is computed SYNCHRONOUSLY from the current selection (NOT the debounced live
 * estimate), so a consumer that persists from this payload never mixes new
 * dimensions with a stale unit and never depends on the 500ms preview timer.
 */
export interface CarbonImpactResolved {
  /** True when a factor resolves, required dims are filled, quantity > 0, and the
   *  quantity is expressible in the factor's input unit — i.e. safe to persist. */
  ready: boolean;
  emissionTableId: string;
  /** Effective dimensions (user picks merged with any locked/pinned dims). */
  dimensionValues: Record<string, string>;
  quantity: number;
  quantityUnit: string;
  /** Quantity converted to the factor's input unit — the multiplier to persist.
   *  Null when not `ready` (or the unit conversion is impossible). */
  quantityNormalized: number | null;
  /** The factor's native input unit. */
  factorInputUnitCode: string | null;
  emissionFactor: number | null;
  /** Debounced live-estimate preview only — never gate persistence on this. */
  calculatedKg: number | null;
  unitOfMeasure: string | null;
}

interface CarbonImpactPickerProps {
  value: CarbonImpactValue;
  onChange: (next: CarbonImpactValue) => void;
  /** Reports the resolved factor (ready + normalized quantity + input unit) up. */
  onResolvedChange?: (resolved: CarbonImpactResolved) => void;
  /**
   * Dimensions pinned at the account level (`carbon_account_emission_tables.
   * dimension_filter`). These are always merged into the resolution (they win on
   * conflict) and are NOT rendered as editable dropdowns — so an account's pinned
   * factor stays deterministic. Empty for the activity form.
   */
  lockedDimensions?: Record<string, string>;
  errors?: Partial<Record<'emissionTableId' | 'quantity' | 'quantityUnit', string>>;
  disabled?: boolean;
  className?: string;
}

const omitKeys = (obj: Record<string, string>, keys: string[]): Record<string, string> =>
  Object.fromEntries(Object.entries(obj).filter(([k]) => !keys.includes(k)));

/**
 * The shared carbon-impact factor finder + quantity input, extracted from the
 * "Log Sustainability Activity" form so the expense / AR-invoice line items use
 * the same machinery (docs/SCOPE3-CARBON-CALCULATOR.md — unified write path).
 *
 * Owns everything about resolving ONE emission factor and computing its impact:
 * the emission-factor table picker, the "Look up on Climatiq" snapshot flow, the
 * left-to-right cascading dimension dropdowns (progressive disclosure), the
 * resolved-factor detail panel, the dimension-resolved compatible-unit dropdown,
 * the quantity input, and the debounced live estimate. It is CONTROLLED: the
 * parent holds the `value` (table + dimensions + quantity + unit) and receives
 * `onChange`; `onResolvedChange` reports the resolved factor's input unit + the
 * live estimate so the parent can normalize the quantity for persistence.
 */
export function CarbonImpactPicker({
  value,
  onChange,
  onResolvedChange,
  lockedDimensions,
  errors,
  disabled = false,
  className,
}: CarbonImpactPickerProps) {
  const { t } = useTranslation('sustainability');
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const saveFactorPick = useSaveEmissionFactorPick();

  const { emissionTableId, dimensionValues, quantity, quantityUnit } = value;

  const lockedCodes = useMemo(() => Object.keys(lockedDimensions ?? {}), [lockedDimensions]);

  // Call the LATEST onResolvedChange from effects without adding it to deps (its
  // identity changes every render at most call sites).
  const onResolvedRef = useRef(onResolvedChange);
  onResolvedRef.current = onResolvedChange;

  const [climatiqOpen, setClimatiqOpen] = useState(false);
  const [importingFactor, setImportingFactor] = useState(false);
  const [factorDetailsOpen, setFactorDetailsOpen] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationResult, setCalculationResult] = useState<{
    emission_factor: number;
    calculated_emission: number;
    unit_of_measure: string;
  } | null>(null);
  const calculationTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const patch = (partial: Partial<CarbonImpactValue>) => onChange({ ...value, ...partial });

  // Emission-factor tables: every GLOBAL table plus this org's own, off the
  // shared index (one cache entry, one endpoint, the same rows the settings
  // grid shows). The 'picker' sub-key and its private select are gone — they
  // were a second, subtly different scope predicate over the same table.
  //
  // ACTIVE-ONLY is filtered HERE, not in the hook: the settings grid needs
  // inactive rows for its "show inactive" toggle, while picking a soft-deleted
  // table would resolve to no factor at all.
  const { data: allEmissionTables } = useEmissionFactorTableIndex(selectedOrganization?.id);
  const emissionTables = useMemo(
    () => (allEmissionTables ?? []).filter((tbl) => tbl.is_active),
    [allEmissionTables],
  );

  const { data: columns } = useCarbonTableColumns(emissionTableId);
  const { data: columnValues } = useCarbonColumnValues(columns);
  const { data: factorDimRows } = useFactorDimensionRows(emissionTableId);

  // The current table's data is fully loaded only once BOTH its columns and its
  // factor rows have arrived. On a table switch `emissionTableId` flips instantly
  // but these queries refetch, so there's a window where they're `undefined`.
  const tableDataReady = !!columns && !!factorDimRows;

  // The cascade INPUT: the user's picks — ignored until the table's data has loaded,
  // so a previous table's selection can't feed the cascade mid-switch — merged with
  // any account-pinned dims (pins win). Column-scoping (dropping keys that aren't
  // columns of this table) happens INSIDE computeDimensionCascade, so this picker and
  // the AccountCarbonSettings caller both get it from one tested place.
  const requestedDims = useMemo(
    () => ({ ...(tableDataReady ? dimensionValues : {}), ...(lockedDimensions ?? {}) }),
    [tableDataReady, dimensionValues, lockedDimensions],
  );

  // Left-to-right cascading dimension filters with progressive disclosure. See
  // computeDimensionCascade — scopes the selection to this table's columns, narrows
  // each select to values valid given the selections to its left, and auto-applies
  // single-value dims. `hasUnresolvedNoneChoice`: a real, deliberate Value-vs-None
  // ambiguity (see dimensionCascade.ts) that must be resolved before persisting —
  // otherwise `ready` could go true with the choice still unmade, and
  // lookup_emission_factor's containment match would silently resolve to whichever
  // row wins its effective_date tie-break. Scoped ONLY to columns carrying the None
  // sentinel, so it doesn't touch the separately-accepted tolerance for ordinary
  // multi-valued optional dims (e.g. the Climatiq waste table).
  const { optionsByCode, cleanedDims, visibleCodes, applicableCodes, hasUnresolvedNoneChoice } =
    useMemo(
      () => computeDimensionCascade(columns, factorDimRows, requestedDims),
      [columns, factorDimRows, requestedDims],
    );

  // `cleanedDims` is the authoritative resolved selection (scoped + pruned +
  // auto-applied). Resolution, estimate, the persisted report, and the read-only
  // display all key off it, so none can mix in a value invalid for the current table.
  const effectiveDims = cleanedDims;

  const { data: resolvedFactor } = useResolvedFactor(emissionTableId, effectiveDims);
  const { units: compatibleUnits, factorInputUnitCode } =
    useCompatibleMultiplierUnitsForDimensions(emissionTableId, effectiveDims);

  const selectedTable = emissionTables.find((tbl) => tbl.id === emissionTableId);

  // Sync the pruned/auto-applied selection back up (reaches a fixpoint: a second
  // pass over cleanedDims yields the same object, so no update loop). Only the
  // NON-locked dims are written to `value` — pinned dims are owned by the prop.
  //
  // Deferred until the current table's data has loaded: syncing mid-switch would
  // either persist a stale/partial selection or, in edit mode, clobber the stored
  // dimensions with `{}` before they can be validated against the loaded table.
  useEffect(() => {
    if (!tableDataReady) return;
    const cleanedUserDims = omitKeys(cleanedDims, lockedCodes);
    if (JSON.stringify(cleanedUserDims) !== JSON.stringify(dimensionValues)) {
      patch({ dimensionValues: cleanedUserDims });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(cleanedDims), tableDataReady]);

  // Default the unit to the resolved factor's input unit when the current one
  // isn't compatible. Keyed on the dimension selection (not just the unit code)
  // so it re-runs when a dimension pick changes the resolved factor.
  useEffect(() => {
    if (!factorInputUnitCode || compatibleUnits.length === 0) return;
    const currentIsCompatible = compatibleUnits.some((u) => u.code === quantityUnit);
    if (!currentIsCompatible) {
      patch({ quantityUnit: factorInputUnitCode });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factorInputUnitCode, compatibleUnits.length, emissionTableId, JSON.stringify(effectiveDims)]);

  // Debounced live estimate — for PREVIEW display only. Persistence never depends
  // on this (see the synchronous report effect below).
  const effectiveDimsKey = useMemo(() => JSON.stringify(effectiveDims), [effectiveDims]);
  useEffect(() => {
    if (calculationTimeoutRef.current) clearTimeout(calculationTimeoutRef.current);

    const run = async () => {
      if (!emissionTableId || !columns || quantity <= 0) {
        setCalculationResult(null);
        return;
      }
      // Required-but-sparse dims (a column marked required at the table level but
      // absent from every candidate row given the current selection) don't block —
      // see computeDimensionCascade's `applicableCodes` doc comment.
      const allRequiredFilled = columns
        .filter((col) => col.is_required && applicableCodes.has(col.column_code))
        .every((col) => effectiveDims[col.column_code]);
      if (!allRequiredFilled || hasUnresolvedNoneChoice) {
        setCalculationResult(null);
        return;
      }

      setIsCalculating(true);
      try {
        const result = await calculateCarbonEmission(
          emissionTableId,
          effectiveDims,
          quantity,
          quantityUnit || null,
        );
        setCalculationResult(result);
      } catch (error) {
        // Plain async (not React Query) — the global handler won't see it. A
        // unit/dimension mismatch (IncompatibleUnitsError) surfaces here.
        console.error('Failed to calculate emissions:', error);
        captureException(error as Error, {
          context: 'CarbonImpactPicker.calculatePreview',
          emissionTableId,
          quantityUnit,
        });
        setCalculationResult(null);
      } finally {
        setIsCalculating(false);
      }
    };

    calculationTimeoutRef.current = setTimeout(run, 500);
    return () => {
      if (calculationTimeoutRef.current) clearTimeout(calculationTimeoutRef.current);
    };
    // factorDimRows is included (not just effectiveDimsKey) because a refetch can
    // change applicableCodes/hasUnresolvedNoneChoice — which gate this preview —
    // without changing effectiveDims's own JSON (e.g. a required column goes from
    // 0 to 2 populated rows: newly blocking, but nothing auto-applies at size 2,
    // so cleanedDims is unchanged). Without this the preview can go stale until
    // some unrelated dependency happens to change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveDimsKey, quantity, quantityUnit, emissionTableId, columns?.length, factorDimRows]);

  // ── Authoritative, SYNCHRONOUS persistence report ─────────────────────────
  // Computed from the current selection, independent of the debounced estimate:
  //  - required dims filled, a factor resolves, quantity > 0,
  //  - the quantity is expressible in the factor's input unit (or the factor is
  //    unitless — no input_unit_code — in which case the raw quantity is used).
  // `quantityNormalized` is derived here from the CURRENT factor unit, so a
  // consumer persisting from this payload never mixes new dims with a stale unit.
  // Same required-but-sparse carve-out as the preview gate above, plus the
  // mandatory Value-vs-None choice (hasUnresolvedNoneChoice).
  const requiredFilled =
    (columns ?? [])
      .filter((col) => col.is_required && applicableCodes.has(col.column_code))
      .every((col) => effectiveDims[col.column_code]) && !hasUnresolvedNoneChoice;
  const unitSatisfied = factorInputUnitCode ? !!quantityUnit : true;
  let quantityNormalized: number | null = null;
  // Gate on tableDataReady: mid-switch `columns` is undefined (so `requiredFilled`
  // is vacuously true) while `useResolvedFactor` still returns the PREVIOUS table's
  // factor via keepPreviousData — without this a report could go `ready` carrying the
  // old factor with empty dimensions and be persisted for the new table.
  if (tableDataReady && requiredFilled && !!resolvedFactor && quantity > 0 && unitSatisfied) {
    if (factorInputUnitCode && quantityUnit && quantityUnit !== factorInputUnitCode) {
      try {
        quantityNormalized = convertValue(quantity, quantityUnit, factorInputUnitCode);
      } catch {
        // Incompatible units — not persistable; the preview/toast surfaces it.
        quantityNormalized = null;
      }
    } else {
      quantityNormalized = quantity;
    }
  }
  const ready = quantityNormalized != null;

  useEffect(() => {
    onResolvedRef.current?.({
      ready,
      emissionTableId,
      dimensionValues: effectiveDims,
      quantity,
      quantityUnit,
      quantityNormalized,
      factorInputUnitCode: factorInputUnitCode ?? null,
      emissionFactor: resolvedFactor?.emission_value ?? calculationResult?.emission_factor ?? null,
      calculatedKg: calculationResult?.calculated_emission ?? null,
      unitOfMeasure: resolvedFactor?.unit_of_measure ?? calculationResult?.unit_of_measure ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ready,
    quantityNormalized,
    emissionTableId,
    effectiveDimsKey,
    quantity,
    quantityUnit,
    factorInputUnitCode,
    resolvedFactor?.emission_value,
    calculationResult?.calculated_emission,
  ]);

  const handleTableChange = (newTableId: string) => {
    // User picked a different table — clear dimensions + unit (a fresh factor),
    // keep the quantity. The Climatiq path sets dims explicitly via patch(...).
    onChange({
      ...value,
      emissionTableId: newTableId,
      dimensionValues: {},
      quantityUnit: '',
    });
    setCalculationResult(null);
  };

  const handleDimensionChange = (columnCode: string, dimValue: string) => {
    patch({ dimensionValues: { ...dimensionValues, [columnCode]: dimValue } });
  };

  const getValuesForColumn = (columnId: string) => {
    const realValues = columnValues?.filter((v) => v.column_id === columnId) || [];
    // Optional select columns also offer an explicit "(None)" choice — see
    // DIMENSION_NONE_VALUE / withNoneOption. Included here (not just as a
    // rendering-time prepend) so any "is this a real value" check against
    // this list stays consistent with what's actually selectable/matchable.
    const column = (columns ?? []).find((c) => c.id === columnId);
    return withNoneOption(
      realValues,
      column?.is_required,
      () => ({
        id: DIMENSION_NONE_VALUE,
        column_id: columnId,
        value_code: DIMENSION_NONE_VALUE,
        display_label: t('form.none'),
      }),
      (v) => v.value_code,
    );
  };

  // Editable dimensions: visible per the cascade AND not pinned at the account
  // level (locked dims are applied via effectiveDims but never user-editable).
  const selectableColumns = useMemo(
    () =>
      (columns ?? []).filter(
        (col) => visibleCodes.has(col.column_code) && !lockedCodes.includes(col.column_code),
      ),
    [columns, visibleCodes, lockedCodes],
  );

  // Dimensions that are FIXED for this row (have a value but aren't user-editable):
  // pinned at the account level, or auto-applied because only one value is valid.
  // Shown read-only so the full selected row is visible — the cascade otherwise
  // hides these entirely. Reads from `cleanedDims` (the cascade's scoped + pruned +
  // auto-applied output), so a value invalid for the current table can never display
  // — this is what stops a previous table's dimension flashing in on a switch.
  const fixedColumns = useMemo(() => {
    const editable = new Set(selectableColumns.map((c) => c.column_code));
    return (columns ?? []).filter(
      (col) => cleanedDims[col.column_code] && !editable.has(col.column_code),
    );
  }, [columns, selectableColumns, cleanedDims]);

  const dimensionValueLabel = (columnId: string, valueCode: string) =>
    resolveDimensionValueLabel(
      valueCode,
      columnValues?.find((v) => v.column_id === columnId && v.value_code === valueCode)
        ?.display_label,
      t('form.none'),
    );

  // Select a (possibly just-created) table + its dimension values atomically.
  const selectResolvedFactor = (tableId: string, dims: Record<string, string>) => {
    onChange({ ...value, emissionTableId: tableId, dimensionValues: dims });
  };

  // Look up a factor on Climatiq and snapshot it into the org's per-sector
  // library (save_org_emission_factor); then auto-select it so it resolves here.
  const handleClimatiqPicked = async (factors: PickedEmissionFactor[]) => {
    if (!selectedOrganization?.id || factors.length === 0) return;
    setImportingFactor(true);
    try {
      const first = factors[0];
      // A LOCAL pick is an already-saved factor — select it in its own table.
      if (first.provider === 'local') {
        const { data: existing } = await supabase
          .from('carbon_emission_factors')
          .select('table_id, dimension_values')
          .eq('id', first.external_activity_id)
          .maybeSingle();
        if (!existing?.table_id) {
          toast.error(t('form.factorImportFailed'));
          return;
        }
        const dims = Object.fromEntries(
          Object.entries((existing.dimension_values as Record<string, unknown>) ?? {}).map(
            ([k, v]) => [k, String(v)],
          ),
        );
        invalidateEmissionFactorCaches(queryClient, selectedOrganization.id);
        selectResolvedFactor(existing.table_id, dims);
        toast.success(t('form.factorSelected'));
        setClimatiqOpen(false);
        return;
      }

      // A CLIMATIQ pick → snapshot into the org's per-sector library. One factor
      // per available year; region/year/data source/LCA each become a dimension.
      const sector = first.sector?.trim() || 'Other';
      const tableCode = `climatiq_${slugify(sector)}`;
      let selectDims: Record<string, string> | null = null;
      for (const factor of factors) {
        const categorySlug =
          slugify(factor.factor_name) || slugify(factor.external_activity_id) || 'factor';
        const regionVal = factor.region_name?.trim() || factor.region?.trim() || null;
        const yearVal = factor.year != null ? String(factor.year) : null;
        const dataSourceVal = factor.data_source?.trim() || null;
        const lcaVal = prettyLca(factor.source_lca_activity) || null;
        const factorDims: Record<string, string | number> = { category: categorySlug };
        if (regionVal) factorDims.region = regionVal;
        if (yearVal) factorDims.year = yearVal;
        if (dataSourceVal) factorDims.data_source = dataSourceVal;
        if (lcaVal) factorDims.lca_scope = lcaVal;
        await saveFactorPick.mutateAsync({
          dimension_values: factorDims,
          factor_name: factor.factor_name,
          emission_value: factor.emission_value,
          unit_of_measure: factor.unit_of_measure,
          input_unit_code: factor.input_unit_code,
          output_unit_code: factor.output_unit_code,
          external_activity_id: factor.external_activity_id,
          external_data_version: factor.external_data_version,
          external_provider: 'climatiq',
          sector,
          category: factor.category ?? null,
        });
        if (!selectDims) {
          selectDims = { category: categorySlug };
          if (regionVal) selectDims.region = regionVal;
          if (dataSourceVal) selectDims.data_source = dataSourceVal;
          if (lcaVal) selectDims.lca_scope = lcaVal;
        }
      }
      // Climatiq factors are saved to the shared GLOBAL library
      // (organization_id NULL / is_system_table true), so read the table back by
      // its global identity — not the org id (which would now match nothing and
      // silently skip the auto-select below). The single-global-per-code unique
      // index guarantees this resolves to exactly the table we just wrote.
      const { data: tbl } = await supabase
        .from('carbon_emission_factor_tables')
        .select('id')
        .is('organization_id', null)
        .eq('table_code', tableCode)
        .maybeSingle();
      // One call, the shared list — `useSaveEmissionFactorPick` already fires it
      // per snapshot above, but the table read-back below needs the tables index
      // fresh too, and the invalidator is idempotent.
      invalidateEmissionFactorCaches(queryClient, selectedOrganization.id);
      if (tbl?.id && selectDims) selectResolvedFactor(tbl.id, selectDims);
      toast.success(t('form.factorImported'));
      setClimatiqOpen(false);
    } catch (error) {
      captureException(error as Error, { context: 'CarbonImpactPicker.climatiqLookup' });
      toast.error(t('form.factorImportFailed'));
    } finally {
      setImportingFactor(false);
    }
  };

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-3">
        <Leaf className="h-5 w-5 text-success" />
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('form.carbonCalculation')}
        </h3>
      </div>

      {/* Emission factor table + Climatiq lookup */}
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Label className="font-extralight">{t('form.emissionFactorTable')}</Label>
          {errors?.emissionTableId && (
            <span className="text-xs text-destructive">{errors.emissionTableId}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Select value={emissionTableId} onValueChange={handleTableChange} disabled={disabled}>
            <SelectTrigger className={`flex-1 ${errors?.emissionTableId ? 'border-destructive' : ''}`}>
              <SelectValue placeholder={t('form.selectEmissionTable')} />
            </SelectTrigger>
            <SelectContent>
              {emissionTables.map((table) => (
                <SelectItem key={table.id} value={table.id}>
                  <div className="flex items-center gap-2">
                    {table.table_name}
                    {/* System = built-in reference table (Climatiq / national
                        datasets), not the org's own. The actual data source
                        (Climatiq, CO2emissiefactoren.nl, …) shows in
                        Settings → Emission Factors. Matches the settings grid's
                        System/Custom badge convention. */}
                    {table.is_system_table && (
                      <Badge variant="secondary" className="text-xs">System</Badge>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            onClick={() => setClimatiqOpen(true)}
            disabled={disabled || importingFactor}
            className="shrink-0"
          >
            {importingFactor ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            {t('form.lookupOnClimatiq')}
          </Button>
        </div>
        {selectedTable?.description && (
          // Fill the full width under the dropdown and wrap, WITHOUT stretching an
          // enclosing min-w-max table (expense/AR line items): `w-0` keeps the
          // text from contributing to the table's intrinsic width, `min-w-full`
          // makes it render at 100% of the available width.
          <p className="text-xs text-muted-foreground mt-1 w-0 min-w-full break-words">
            {selectedTable.description}
          </p>
        )}
      </div>

      {/* Cascading dimension inputs */}
      {selectableColumns.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 mt-4 border-t">
          {selectableColumns.map((column) => (
            <div key={column.id} className="space-y-1">
              <Label htmlFor={`dimension-${column.column_code}`} className="text-sm">
                {column.display_label}
                {column.is_required && <span className="text-destructive ml-1">*</span>}
              </Label>
              {column.column_type === 'select' ? (
                <Select
                  value={cleanedDims[column.column_code] || ''}
                  onValueChange={(v) => handleDimensionChange(column.column_code, v)}
                  disabled={disabled}
                >
                  <SelectTrigger id={`dimension-${column.column_code}`}>
                    <SelectValue placeholder={t('form.select')} />
                  </SelectTrigger>
                  <SelectContent>
                    {getValuesForColumn(column.id)
                      .filter((v) => optionsByCode[column.column_code]?.has(v.value_code) ?? true)
                      .map((v) => (
                        <SelectItem key={v.id} value={v.value_code}>
                          {v.display_label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              ) : column.column_type === 'numeric' ? (
                <NumberInput
                  id={`dimension-${column.column_code}`}
                  value={cleanedDims[column.column_code] || ''}
                  onChange={(v) => handleDimensionChange(column.column_code, String(v))}
                  required={column.is_required}
                  disabled={disabled}
                />
              ) : (
                <Input
                  id={`dimension-${column.column_code}`}
                  type="text"
                  value={cleanedDims[column.column_code] || ''}
                  onChange={(e) => handleDimensionChange(column.column_code, e.target.value)}
                  required={column.is_required}
                  disabled={disabled}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Fixed dimensions — pinned at the account level (lock icon) or auto-applied
          because only one value is valid. Read-only, so the complete selected row
          is always visible even though the cascade hides these from the pickers. */}
      {fixedColumns.length > 0 && (
        <div className="pt-4 mt-4 border-t">
          <p className="text-xs font-medium text-muted-foreground mb-2">
            {t('form.fixedDimensions')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {fixedColumns.map((column) => {
              const code = cleanedDims[column.column_code];
              const pinned = lockedCodes.includes(column.column_code);
              return (
                <div key={column.id} className="rounded-md border bg-muted/30 px-3 py-2">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    {pinned && <Lock className="h-3 w-3" />}
                    {column.display_label}
                  </div>
                  <div className="text-sm font-medium">
                    {dimensionValueLabel(column.id, code)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Resolved emission-factor details — collapsed by default */}
      {resolvedFactor && (
        <Collapsible open={factorDetailsOpen} onOpenChange={setFactorDetailsOpen} className="pt-4 mt-4 border-t">
          <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 rounded-md p-2 text-sm font-medium transition-colors hover:bg-muted/50">
            <span>{t('form.factorDetails')}</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${factorDetailsOpen ? 'rotate-180' : ''}`} />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-1 rounded-md border bg-muted/30 p-3 space-y-2 text-sm">
              <div className="font-medium">{resolvedFactor.factor_name}</div>
              <dl className="space-y-1">
                {(columns ?? [])
                  .filter((col) => cleanedDims[col.column_code])
                  .map((col) => {
                    const code = cleanedDims[col.column_code];
                    const label = resolveDimensionValueLabel(
                      code,
                      columnValues?.find((v) => v.column_id === col.id && v.value_code === code)
                        ?.display_label,
                      t('form.none'),
                    );
                    return (
                      <div key={col.id} className="flex justify-between gap-4">
                        <dt className="text-muted-foreground">{col.display_label}</dt>
                        <dd className="text-right">{label}</dd>
                      </div>
                    );
                  })}
                <div className="flex justify-between gap-4 pt-1 border-t">
                  <dt className="text-muted-foreground">{t('form.factorValue')}</dt>
                  <dd className="text-right font-mono">
                    <NumberDisplay
                      value={resolvedFactor.emission_value}
                      decimals={6}
                      unit={resolvedFactor.unit_of_measure}
                    />
                  </dd>
                </div>
              </dl>
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Quantity + unit */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 mt-4 border-t">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Label className="font-extralight">{t('form.quantity')}</Label>
            {errors?.quantity && <span className="text-xs text-destructive">{errors.quantity}</span>}
          </div>
          <NumberInput
            className={errors?.quantity ? 'border-destructive' : ''}
            value={quantity}
            onChange={(v) => patch({ quantity: typeof v === 'number' ? v : 0 })}
            disabled={disabled}
          />
        </div>

        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Label className="font-extralight">{t('form.unit')}</Label>
            {errors?.quantityUnit && (
              <span className="text-xs text-destructive">{errors.quantityUnit}</span>
            )}
          </div>
          {factorInputUnitCode ? (
            <Select
              onValueChange={(v) => patch({ quantityUnit: v })}
              value={quantityUnit}
              disabled={disabled || !emissionTableId}
            >
              <SelectTrigger className={errors?.quantityUnit ? 'border-destructive' : ''}>
                <SelectValue placeholder={t('form.selectUnit')} />
              </SelectTrigger>
              <SelectContent>
                {compatibleUnits.map((u) => (
                  <SelectItem key={u.code} value={u.code}>
                    {u.label} ({u.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : emissionTableId ? (
            <div className="p-2 bg-warning/10 border border-warning/30 rounded-md text-xs flex items-start gap-2">
              <Info className="h-4 w-4 text-warning mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-warning">{t('form.missingUnitTitle')}</p>
                <p className="text-muted-foreground mt-1">{t('form.missingUnitDescription')}</p>
              </div>
            </div>
          ) : (
            <Select value={quantityUnit} disabled>
              <SelectTrigger>
                <SelectValue placeholder={t('form.selectFactorTableFirst')} />
              </SelectTrigger>
            </Select>
          )}
        </div>
      </div>

      {factorInputUnitCode && quantityUnit && quantityUnit !== factorInputUnitCode && (
        <div className="flex items-start gap-2 p-2 mt-3 bg-info/10 border border-info/30 rounded-md text-xs">
          <Info className="h-4 w-4 text-info mt-0.5 flex-shrink-0" />
          <p className="text-info">
            <Trans
              i18nKey="form.unitConversionNote"
              ns="sustainability"
              values={{ fromUnit: unitLabel(quantityUnit), toUnit: unitLabel(factorInputUnitCode) }}
              components={{ strong: <strong /> }}
            />
          </p>
        </div>
      )}

      {/* Live estimate */}
      <div className="pt-4 mt-4 border-t">
        {calculationResult ? (
          <div className={`space-y-2 ${isCalculating ? 'opacity-60 transition-opacity' : ''}`}>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-success" />
              <span className="text-sm font-medium text-success">{t('form.calculationComplete')}</span>
            </div>
            <div className="p-3 bg-success/10 border border-success/30 rounded-md space-y-2">
              <div className="flex justify-between items-baseline text-sm">
                <span className="text-success">{t('form.emissionFactor')}</span>
                <span className="font-mono font-medium text-success">
                  <NumberDisplay
                    value={calculationResult.emission_factor}
                    decimals={6}
                    unit={calculationResult.unit_of_measure}
                  />
                </span>
              </div>
              <div className="flex justify-between items-baseline text-sm">
                <span className="text-success">{t('form.calculation')}</span>
                <span className="font-mono text-success text-xs">
                  <NumberDisplay value={calculationResult.emission_factor} decimals={4} /> ×{' '}
                  <NumberDisplay value={quantity} decimals={2} /> ={' '}
                  <NumberDisplay value={calculationResult.calculated_emission} decimals={4} />
                </span>
              </div>
              <div className="flex justify-between items-baseline pt-2 border-t border-success/30">
                <span className="text-sm font-medium text-success">{t('form.totalEmissions')}</span>
                <Badge className="bg-success text-success-foreground font-mono text-sm">
                  <NumberDisplay
                    value={calculationResult.calculated_emission}
                    decimals={4}
                    unit="kg CO2e"
                  />
                </Badge>
              </div>
            </div>
          </div>
        ) : isCalculating ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{t('form.calculatingEmissions')}</span>
          </div>
        ) : emissionTableId ? (
          <p className="text-sm text-muted-foreground">{t('form.fillRequiredFields')}</p>
        ) : null}
      </div>

      {climatiqOpen && (
        <EmissionFactorPickerDialog
          open
          onOpenChange={setClimatiqOpen}
          defaultQuery={selectedTable?.table_name ?? ''}
          onPicked={handleClimatiqPicked}
        />
      )}
    </div>
  );
}
